import React, { useEffect, useState, useMemo } from 'react';
import { Pencil, ShieldAlert, ShieldCheck, MoreVertical, Search, Plus } from 'lucide-react';
import useComponentVisible from '../UI/ComponentVisible';
import { useUserContext } from '../../contexts/UserContext';
import { adminDeactivateUser, getAllUsers, adminUpdateUserProfile } from '../../utils/APIs';
import { formatDate } from '../../utils/converter';
import UpdateUserModal from './UpdateUserModal';
import InviteUserModal from './InviteUserModal';
import UpdatePassword from '../ManageUser/UpdatePassword';
import { toast } from 'react-toastify';
import ConfirmModal from '../UI/Modal/confirmModal';
import PrimaryModal from '../UI/Modal/PrimaryModal';
import { useContentfulContext } from '../../contexts/ContentfulContext';
import { getAgGridSortStorageKey } from '../UI/AgGrid';
import { Button, Input, AgTableCard } from '../UI';


const UserManagement = () => {
    const { ref, isComponentVisible, setIsComponentVisible } = useComponentVisible(false);
    const { content } = useContentfulContext();

    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
    const [loading, setLoading] = useState(false);
    const [selectedAction, setSelectedAction] = useState('');
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const { userData } = useUserContext();
    const [gridApi, setGridApi] = useState(null);

    const getAllOrgUsers = async () => {
        try {
            setLoading(true)
            const res = await getAllUsers(userData?.token, userData?.selectedOrganization?.org_token)
            if (res.status) {
                setUsers(res.data)
            }
        } catch (error) {
            setLoading(false)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        getAllOrgUsers()
    }, [])

    const colDefs = [
        {
            headerName: content.USER_MANAGEMENT.table_headers.user_name,
            field: "name",
            filter: true,
            cellRenderer: ({ data }) => (
                <button
                    type="button"
                    className="font-medium text-primary hover:text-primary-hover hover:underline text-left cursor-pointer"
                    onClick={() => {
                        setSelectedUser(data);
                        setSelectedAction("updateUser");
                    }}
                >
                    {data.name}
                </button>
            ),
            suppressMovable: true
        },
        {
            headerName: content.USER_MANAGEMENT.table_headers.email,
            field: "email",
            cellRenderer: ({ data }) => (
                <span className="text-xs text-stride-body font-medium">{data?.email}</span>
            ),
            comparator: (date1, date2) => new Date(date1) - new Date(date2),
            suppressMovable: true,
            sortable: true
        },
        {
            headerName: 'Status',
            field: 'blocked',
            cellRenderer: ({ data }) => {
                const isBlocked = data?.blocked;
                return (
                    <span className={`stride-badge-pill text-xs font-semibold ${isBlocked ? 'bg-rose-50 text-rose-700 border border-rose-200' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'}`}>
                        {isBlocked ? 'Blocked' : 'Active'}
                    </span>
                );
            },
            width: 110,
            sortable: true,
            suppressMovable: true
        },
        {
            headerName: content.USER_MANAGEMENT.table_headers.updated_at,
            field: "updated_at",
            cellRenderer: ({ data }) => (
                <span className="text-xs text-stride-muted tabular-nums">
                    {formatDate(data?.updated_at)}
                </span>
            ),
            sortable: true,
            suppressMovable: true
        },
        {
            headerName: 'Actions',
            field: 'actions',
            width: 140,
            sortable: false,
            filter: false,
            resizable: false,
            suppressMovable: true,
            cellRenderer: ({ data }) => {
                const user = users.find(x => x.user_id === data.user_id) || data;
                const isBlocked = user?.blocked;
                return (
                    <div className="stride-table-actions">
                        <button
                            type="button"
                            title="Edit user"
                            className="stride-table-btn stride-table-btn-edit"
                            onClick={() => {
                                setSelectedUser(user);
                                setSelectedAction("updateUser");
                            }}
                        >
                            <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                            type="button"
                            title={isBlocked ? "Activate user" : "Deactivate user"}
                            className={`stride-table-btn ${isBlocked ? 'hover:text-emerald-600' : 'hover:text-rose-600'}`}
                            onClick={() => {
                                setSelectedUser(user);
                                setSelectedAction('block');
                            }}
                        >
                            {isBlocked ? <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" /> : <ShieldAlert className="w-3.5 h-3.5 text-rose-600" />}
                        </button>
                        <button
                            type="button"
                            className="stride-table-btn actions"
                            title="More options"
                            onClick={(e) => {
                                e.stopPropagation();
                                setIsComponentVisible(!isComponentVisible);
                                setSelectedUser(user);
                                const buttonRect = e.currentTarget.getBoundingClientRect();
                                setMenuPosition({
                                    top: buttonRect.bottom + window.scrollY,
                                    left: buttonRect.left + window.scrollX
                                });
                            }}
                        >
                            <MoreVertical className="w-3.5 h-3.5" />
                        </button>
                    </div>
                );
            },
        },
    ];

    const onFilterTextBoxChanged = (event) => {
        const filterValue = (event.target.value || '').toLowerCase();
        gridApi.setFilterModel({
            name: { type: 'contains', filter: filterValue }
        });
    };

    const onGridReady = (params) => {
        setGridApi(params.api);

        const storageKey = getAgGridSortStorageKey("admin-user-management-table");
        const sortModel = JSON.parse(localStorage.getItem(storageKey));

        if (!sortModel || sortModel?.length === 0) {
            params.columnApi.applyColumnState({
                state: [
                    { colId: 'email', sort: 'desc' }
                ],
                applyOrder: false,
            });
        }
    };

    const autoSizeStrategy = useMemo(() => {
        return {
            type: "fitGridWidth",
            defaultMinWidth: 120,
            columnLimits: [
                {
                    colId: "name",
                    minWidth: 200,
                },
                {
                    colId: "email",
                    minWidth: 240,
                },
                {
                    colId: "blocked",
                    minWidth: 110,
                },
                {
                    colId: "updated_at",
                    minWidth: 160,
                },
                {
                    colId: "actions",
                    minWidth: 140,
                }
            ],
        };
    }, []);

    const handleActivate = async () => {
        setLoading(true)
        try {
            const res = await adminUpdateUserProfile(userData.token, selectedUser.user_id, {
                blocked: false
            }, userData?.selectedOrganization?.org_token);
            if (res.status) {
                toast.success(res.message);
                setSelectedAction("")
                setTimeout(() => {
                    getAllOrgUsers();
                    setLoading(false);
                }, 2000);
            }
        } catch (error) {
            setLoading(false)
            toast.error("Error activating the user.");
        }
    };


    const handleDeactivate = async () => {
        setLoading(true)
        try {
            const res = await adminDeactivateUser(userData.token, selectedUser.user_id, userData?.selectedOrganization?.org_token);
            if (res.status) {
                toast.success(res.message);
                setSelectedAction("")
                setTimeout(() => {
                    getAllOrgUsers();
                    setLoading(false);
                }, 2000)
            }
        } catch (error) {
            setLoading(false)
            toast.error("Error deactivating the user.");
        }
    };

    return (
        <>
            {selectedAction == 'updateUser' && <UpdateUserModal setSelectedAction={setSelectedAction} selectedUser={selectedUser} getAllOrgUsers={getAllOrgUsers} />}
            {selectedAction == 'createUser' && <InviteUserModal setSelectedAction={() => setSelectedAction("")} />}
            <main id="main" className="stride-page-shell flex flex-col space-y-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                        <span className="stride-eyebrow">Administration</span>
                        <div className="flex items-baseline gap-3">
                            <h1 className="text-2xl font-bold tracking-tight text-stride-heading">
                                {content.USER_MANAGEMENT.title}
                            </h1>
                            <span className="text-xs text-stride-muted font-normal">
                                {users.length} {users.length === 1 ? 'user' : 'users'}
                            </span>
                        </div>
                    </div>

                    <Button
                        variant="primary"
                        icon={<Plus className="w-4 h-4" />}
                        onClick={() => {
                            setSelectedAction("createUser");
                            setSelectedUser(null);
                        }}
                    >
                        {content.USER_MANAGEMENT.cta_text.create_user}
                    </Button>
                </div>

                <AgTableCard
                    header={
                        <div className="w-full sm:w-80">
                            <Input
                                placeholder={content.USER_MANAGEMENT.search_placeholder}
                                onChange={onFilterTextBoxChanged}
                                prefixIcon={<Search className="w-4 h-4 text-stride-muted" />}
                                clearable
                            />
                        </div>
                    }
                    tableId="admin-user-management-table"
                    loading={loading}
                    rowData={users}
                    columnDefs={colDefs}
                    autoSizeStrategy={autoSizeStrategy}
                    onGridReady={onGridReady}
                >
                    {isComponentVisible && (
                        <div
                            ref={ref}
                            className="absolute z-10 mt-2 w-48 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none py-1"
                            style={{ top: menuPosition.top, left: Math.max(10, menuPosition.left - 180) }}
                        >
                            <div className="py-1">
                                <a
                                    href="#"
                                    className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100"
                                    tabIndex="-1"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setIsComponentVisible(false);
                                        setSelectedAction('updateUser');
                                    }}
                                >
                                    {content.USER_MANAGEMENT.more_info.edit}
                                </a>
                                <a
                                    href="#"
                                    className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100"
                                    tabIndex="-1"
                                    onClick={async (e) => {
                                        e.preventDefault();
                                        setIsComponentVisible(false);
                                        setSelectedAction('block');
                                    }}
                                >
                                    {selectedUser?.blocked ? content.USER_MANAGEMENT.more_info.unblock : content.USER_MANAGEMENT.more_info.block}
                                </a>
                                <a
                                    href="#"
                                    className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100"
                                    tabIndex="-1"
                                    onClick={async (e) => {
                                        e.preventDefault();
                                        setIsComponentVisible(false);
                                        setSelectedAction('reset_password');
                                    }}
                                >
                                    {content.USER_MANAGEMENT.more_info.reset_password}
                                </a>
                            </div>
                        </div>
                    )}
                </AgTableCard>

                {selectedAction === "reset_password" && (
                    <PrimaryModal title={"Reset Password"} hideModal={() => setSelectedAction("")}>
                        <UpdatePassword setShowUpdatePassword={() => setSelectedAction("")} selectedUser={selectedUser} />
                    </PrimaryModal>
                )}
                {selectedAction === "block" && (
                    <ConfirmModal
                        title={`Confirm to ${selectedUser?.blocked ? "Activate" : "Deactivate"} this Account`}
                        hideModal={() => setSelectedAction("")}
                        handleConfirmation={selectedUser?.blocked ? handleActivate : handleDeactivate}
                    />
                )}
            </main>
        </>
    );
};

export default UserManagement
